import type { IncomeFormJson } from 'src/services/incomeTypes/formJson.types';
import { MutationFunction } from '@tanstack/react-query';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';
import { IncomeType, IncomeTypeDocument } from './types';
import type { IncomeCategory } from './incomeCategory';

export interface UpdateIncomeTypeParams {
  id: string;
  type?: string;
  documents?: IncomeTypeDocument[];
  incomeCategory?: IncomeCategory;
  inUse?: boolean;
  /** The whole form configuration. The API replaces the column with what it
   *  is given, so send the complete document, never a fragment. */
  formJson?: IncomeFormJson;
}

const updateIncomeType: MutationFunction<
  IncomeType,
  UpdateIncomeTypeParams
> = async ({ id, ...values }) => {
  if (isMockServices()) {
    return await mock({
      id,
      type: values.type ?? 'W-2 Employment Income',
      documents: values.documents ?? [],
      formJson: {},
      incomeCategory: values.incomeCategory ?? null,
      inUse: values.inUse ?? true,
    } as IncomeType);
  }

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<{ success: boolean; data: IncomeType }>(
    `/income-types/${id}`,
    {
      method: 'PUT',
      body: {
        ...(values.type !== undefined && { type: values.type }),
        ...(values.documents !== undefined && { documents: values.documents }),
        ...(values.incomeCategory !== undefined && {
          incomeCategory: values.incomeCategory,
        }),
        ...(values.inUse !== undefined && { inUse: values.inUse }),
        ...(values.formJson !== undefined && { formJson: values.formJson }),
      },
    },
  );

  if (response.data) return response.data.data;
  throw new Error('Failed to update income type');
};

export default updateIncomeType;
